import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Crown, User, LogOut, Gift, ShoppingBag, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useLoyaltyProfile } from "@/hooks/useLoyaltyProfile";
import { toast } from "sonner";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { profile } = useLoyaltyProfile();

  const baseNavItems = [
    { label: "Inicio", path: "/" },
    { label: "Carta", path: "/carta" },
    { label: "Encargos", path: "/encargos" },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    await signOut();
    toast.success('Sesión cerrada');
    navigate('/');
  };

  const getDisplayName = () => {
    if (profile?.first_name) {
      return profile.first_name;
    }
    if (user?.user_metadata?.first_name) {
      return user.user_metadata.first_name;
    }
    return 'Mi Perfil';
  };

  return (
    <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="flex flex-col">
              <span className="font-serif text-xl md:text-2xl font-bold text-primary tracking-wide">
                Pastelería Reyes
              </span>
              <span className="text-xs text-muted-foreground tracking-widest">
                Desde 1979
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {baseNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`font-medium transition-colors hover:text-secondary ${
                  isActive(item.path)
                    ? "text-secondary border-b-2 border-secondary pb-1"
                    : "text-primary"
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* Conditional Club/Profile Link */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className={`font-medium flex items-center gap-1 px-3 ${
                      isActive('/club') ? "text-secondary" : "text-primary hover:text-secondary"
                    }`}
                  >
                    <User className="h-4 w-4" />
                    {getDisplayName()}
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-card border-border">
                  <DropdownMenuItem onClick={() => navigate('/club')} className="cursor-pointer">
                    <Crown className="h-4 w-4 mr-2 text-secondary" />
                    Mis Puntos
                    {profile && (
                      <span className="ml-auto text-xs text-secondary font-semibold">
                        {profile.points_balance}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/encargos')} className="cursor-pointer">
                    <ShoppingBag className="h-4 w-4 mr-2" />
                    Mis Encargos
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="h-4 w-4 mr-2" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/club"
                className={`font-medium transition-colors hover:text-secondary flex items-center gap-1 ${
                  isActive('/club')
                    ? "text-secondary border-b-2 border-secondary pb-1"
                    : "text-primary"
                }`}
              >
                <Crown className="h-4 w-4" />
                Mis Puntos
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-4">
              {baseNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`font-medium py-2 transition-colors ${
                    isActive(item.path)
                      ? "text-secondary"
                      : "text-primary hover:text-secondary"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Mobile Profile/Club section */}
              {user ? (
                <>
                  <div className="border-t border-border pt-4 mt-2">
                    <p className="text-sm text-muted-foreground mb-2">
                      Hola, {getDisplayName()}
                    </p>
                    <Link
                      to="/club"
                      onClick={() => setIsMenuOpen(false)}
                      className={`font-medium py-2 transition-colors flex items-center gap-2 ${
                        isActive('/club')
                          ? "text-secondary"
                          : "text-primary hover:text-secondary"
                      }`}
                    >
                      <Crown className="h-4 w-4" />
                      Mis Puntos
                      {profile && (
                        <span className="ml-auto text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded-full font-semibold">
                          {profile.points_balance}
                        </span>
                      )}
                    </Link>
                    <Link
                      to="/encargos"
                      onClick={() => setIsMenuOpen(false)}
                      className="font-medium py-2 transition-colors flex items-center gap-2 text-primary hover:text-secondary"
                    >
                      <ShoppingBag className="h-4 w-4" />
                      Mis Encargos
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="font-medium py-2 transition-colors flex items-center gap-2 text-destructive hover:text-destructive/80 w-full text-left"
                    >
                      <LogOut className="h-4 w-4" />
                      Cerrar sesión
                    </button>
                  </div>
                </>
              ) : (
                <Link
                  to="/club"
                  onClick={() => setIsMenuOpen(false)}
                  className={`font-medium py-2 transition-colors flex items-center gap-2 ${
                    isActive('/club')
                      ? "text-secondary"
                      : "text-primary hover:text-secondary"
                  }`}
                >
                  <Crown className="h-4 w-4" />
                  Mis Puntos
                </Link>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
